import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { t } from "i18next";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Label,
  Input,
  FormGroup,
  Row,
  Col,
} from "reactstrap";
import { QRCode } from "react-qrcode-logo";
import { saveAs } from "file-saver";
import {
  PAPER_SIZES,
  DEFAULT_PDF_OPTIONS,
  computeGridLayout,
  convertQRToDataUrl,
  generateQrPdf,
} from "../../../../utils/qrPdf";

const QR_BASE_URL = process.env.REACT_APP_QR_URL || "https://qr.clickorder.io";

const toId = (id) => Number(id);

const subspaceQrRows = (space) =>
  (space?.subspaces || []).filter((sub) => sub?.qrKey);

/** Space QR, or only subspace QRs when the space has any. */
const spacePdfItems = (space, includeSubspaces) => {
  const subs = includeSubspaces ? subspaceQrRows(space) : [];
  if (subs.length > 0) {
    const spaceName = space.name || `Space ${space.space_id}`;
    return subs.map((sub) => ({
      id: `sub-${sub.sub_space_id}`,
      name: `${spaceName} - ${sub.name || `Subspace ${sub.sub_space_id}`}`,
      qrKey: sub.qrKey,
      site_image: space.site_image,
    }));
  }
  if (!space?.qrKey) return [];
  return [
    {
      id: `space-${toId(space.space_id)}`,
      name: space.name || `Space ${space.space_id}`,
      qrKey: space.qrKey,
      site_image: space.site_image,
    },
  ];
};

const ExportQRPdfModal = ({ isOpen, toggle, fetchSpaces, siteName }) => {
  const [spaces, setSpaces] = useState([]);
  const [isLoadingSpaces, setIsLoadingSpaces] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [listFilter, setListFilter] = useState("");
  const [includeSubspaces, setIncludeSubspaces] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [renderForExport, setRenderForExport] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [options, setOptions] = useState({ ...DEFAULT_PDF_OPTIONS });
  const qrRefs = useRef({});
  const exportReadyResolver = useRef(null);
  const openedRef = useRef(false);

  const spacesWithQR = useMemo(
    () =>
      (spaces || []).filter(
        (s) => spacePdfItems(s, includeSubspaces).length > 0,
      ),
    [spaces, includeSubspaces],
  );

  const filteredSpaces = useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    if (!q) return spacesWithQR;
    return spacesWithQR.filter((s) => {
      const name = (s.name || "").toLowerCase();
      const site = (s.site_name || "").toLowerCase();
      const id = String(s.space_id);
      return name.includes(q) || site.includes(q) || id.includes(q);
    });
  }, [spacesWithQR, listFilter]);

  const layout = useMemo(() => computeGridLayout(options), [options]);

  const allSelected =
    spacesWithQR.length > 0 && selectedIds.size === spacesWithQR.length;

  const selectedSpaces = useMemo(
    () => spacesWithQR.filter((s) => selectedIds.has(toId(s.space_id))),
    [spacesWithQR, selectedIds],
  );

  const pdfItems = useMemo(
    () =>
      selectedSpaces.flatMap((space) =>
        spacePdfItems(space, includeSubspaces),
      ),
    [selectedSpaces, includeSubspaces],
  );

  const pageCount = Math.max(
    1,
    Math.ceil(pdfItems.length / layout.perPage) || 1,
  );

  const previewItems = useMemo(() => {
    const start = previewPage * layout.perPage;
    return pdfItems.slice(start, start + layout.perPage);
  }, [pdfItems, previewPage, layout.perPage]);

  // Preview QR pixel size roughly proportional to mm size on the page
  const previewQrPx = useMemo(() => {
    const scale = options.orientation === "landscape" ? 560 : 360;
    const pxPerMm = scale / layout.pageWidth;
    return Math.max(24, Math.min(120, Math.round(layout.qrSize * pxPerMm)));
  }, [layout.qrSize, layout.pageWidth, options.orientation]);

  const previewFontPx = useMemo(() => {
    const sheetWidth = options.orientation === "landscape" ? 560 : 360;
    const pxPerMm = sheetWidth / layout.pageWidth;
    // 1pt ≈ 0.3528mm
    return Math.max(6, Math.round(layout.fontSize * 0.3528 * pxPerMm));
  }, [layout.fontSize, layout.pageWidth, options.orientation]);

  useEffect(() => {
    if (!isOpen) openedRef.current = false;
  }, [isOpen]);

  // Load the site's spaces (and subspace QRs when that option is on).
  // Opening the modal preselects every space for the current site filter.
  useEffect(() => {
    if (!isOpen || !fetchSpaces) return;

    const justOpened = !openedRef.current;
    openedRef.current = true;
    const withSubs = justOpened ? false : includeSubspaces;

    if (justOpened) {
      setListFilter("");
      setOptions({ ...DEFAULT_PDF_OPTIONS });
      setPreviewPage(0);
      setIsLoadingSpaces(true);
      if (includeSubspaces) {
        setIncludeSubspaces(false);
        return;
      }
    }

    let cancelled = false;
    const load = async () => {
      setIsLoadingSpaces(true);
      try {
        const data = await fetchSpaces({ includeSubspaces: withSubs });
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setSpaces(list);
        const ids = new Set(
          list
            .filter((s) => spacePdfItems(s, withSubs).length > 0)
            .map((s) => toId(s.space_id)),
        );
        setSelectedIds(ids);
        setPreviewPage(0);
      } catch (error) {
        console.error("Failed to load spaces for PDF export:", error);
        if (!cancelled) {
          setSpaces([]);
          setSelectedIds(new Set());
        }
      } finally {
        if (!cancelled) setIsLoadingSpaces(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, fetchSpaces, includeSubspaces]);

  // Keep preview page in range when selection / grid changes
  useEffect(() => {
    if (previewPage > pageCount - 1) {
      setPreviewPage(Math.max(0, pageCount - 1));
    }
  }, [pageCount, previewPage]);

  // After offscreen QRs mount for export, resolve waiter
  useEffect(() => {
    if (renderForExport && exportReadyResolver.current) {
      const timer = setTimeout(
        () => {
          exportReadyResolver.current?.();
          exportReadyResolver.current = null;
        },
        options.includeLogo ? 1200 : 400,
      );
      return () => clearTimeout(timer);
    }
  }, [renderForExport, options.includeLogo, pdfItems.length]);

  const updateOption = (key, value) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
  };

  const isSelected = (spaceId) => selectedIds.has(toId(spaceId));

  const toggleSpace = (spaceId, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const id = toId(spaceId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(spacesWithQR.map((s) => toId(s.space_id))));
    }
  };

  const selectFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredSpaces.forEach((s) => next.add(toId(s.space_id)));
      return next;
    });
  };

  const waitForExportQrs = () =>
    new Promise((resolve) => {
      exportReadyResolver.current = resolve;
      setRenderForExport(true);
    });

  const handleGenerate = useCallback(async () => {
    if (pdfItems.length === 0) return;

    setIsGenerating(true);
    qrRefs.current = {};
    try {
      await waitForExportQrs();

      const items = [];
      for (const item of pdfItems) {
        const el = qrRefs.current[item.id];
        const dataUrl = await convertQRToDataUrl(el);
        if (dataUrl) {
          items.push({
            name: item.name,
            dataUrl,
          });
        }
      }

      if (items.length === 0) {
        alert(
          t(
            "Failed to generate any QR codes. The logo images may be blocked by CORS policy.",
          ),
        );
        return;
      }

      const safeSite = (siteName || "QR_Codes").replace(/[^\w\-]+/g, "_");
      const fileName = `${safeSite}_QR_${Number(layout.cellWidth)}x${Number(layout.cellHeight)}mm_${Date.now()}.pdf`;

      // Pass the exact pitch shown in the UI — never trust a stale default
      const { blob, layout: used } = await generateQrPdf({
        items,
        options: {
          ...options,
          cellWidth: Number(layout.cellWidth),
          cellHeight: Number(layout.cellHeight),
          cellPadding: Number(layout.cellPadding),
          pageMargin: Number(layout.pageMargin),
        },
      });

      saveAs(blob, fileName);

      console.info(
        `[QR PDF] Outer cell line-to-line: ${used.cellWidth} × ${used.cellHeight} mm`,
        used,
      );

      if (items.length < pdfItems.length) {
        alert(
          t(
            `Successfully exported ${items.length} out of ${pdfItems.length} QR codes.`,
          ),
        );
      }
    } catch (error) {
      console.error("Error generating QR PDF:", error);
      alert(t("Failed to generate PDF. Please try again."));
    } finally {
      setRenderForExport(false);
      setIsGenerating(false);
    }
  }, [pdfItems, options, siteName, layout]);

  const previewAspect = `${layout.pageWidth} / ${layout.pageHeight}`;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle}>{t("Export QR PDF")}</ModalHeader>
      <ModalBody>
        {isLoadingSpaces ? (
          <div className="text-center py-5">
            <Spinner />
            <p className="text-muted mt-2 mb-0">{t("Loading spaces...")}</p>
          </div>
        ) : spacesWithQR.length === 0 ? (
          <div className="text-center p-4">
            <p className="text-muted mb-0">
              {t("No spaces with QR codes found")}
            </p>
          </div>
        ) : (
          <Row className="g-3">
            <Col lg="5">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="form-check mb-0">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    id="pdfSelectAll"
                  />
                  <Label className="form-check-label" htmlFor="pdfSelectAll">
                    {t("Select All")} ({spacesWithQR.length})
                  </Label>
                </div>
                <small className="text-muted">
                  {selectedIds.size} {t("selected")}
                  {includeSubspaces ? ` · ${pdfItems.length} ${t("QR codes")}` : ""}
                </small>
              </div>

              <div className="form-check mb-2">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id="pdfIncludeSubspaces"
                  checked={includeSubspaces}
                  disabled={isLoadingSpaces}
                  onChange={(e) => setIncludeSubspaces(e.target.checked)}
                />
                <Label className="form-check-label" htmlFor="pdfIncludeSubspaces">
                  {t("Include subspaces")}
                </Label>
                <small className="text-muted d-block">
                  {t(
                    "A space with subspaces exports only those QR codes, named Space - Subspace.",
                  )}
                </small>
              </div>

              <Input
                type="search"
                bsSize="sm"
                className="mb-2"
                placeholder={t("Filter spaces...")}
                value={listFilter}
                onChange={(e) => setListFilter(e.target.value)}
              />

              {listFilter.trim() !== "" && (
                <div className="mb-2">
                  <Button
                    size="sm"
                    color="link"
                    className="p-0"
                    onClick={selectFiltered}
                  >
                    {t("Select filtered")} ({filteredSpaces.length})
                  </Button>
                </div>
              )}

              <div
                className="border rounded mb-3"
                style={{ maxHeight: 220, overflowY: "auto" }}
              >
                {filteredSpaces.length === 0 ? (
                  <div className="text-center text-muted p-3 small">
                    {t("No matching spaces")}
                  </div>
                ) : (
                  filteredSpaces.map((space) => {
                    const id = toId(space.space_id);
                    const checked = isSelected(id);
                    const inputId = `pdf-space-${id}`;
                    return (
                      <label
                        key={id}
                        htmlFor={inputId}
                        className="d-flex align-items-center px-2 py-2 border-bottom mb-0"
                        style={{
                          gap: 10,
                          cursor: "pointer",
                          backgroundColor: checked ? "#f8f1e8" : undefined,
                          userSelect: "none",
                        }}
                      >
                        <Input
                          id={inputId}
                          className="form-check-input m-0 flex-shrink-0"
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => toggleSpace(id, e)}
                          style={{ position: "static", marginTop: 0 }}
                        />
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="fw-medium text-truncate">
                            {space.name}
                          </div>
                          <small className="text-muted">
                            ID: {space.space_id}
                            {space.site_name ? ` · ${space.site_name}` : ""}
                            {includeSubspaces &&
                            subspaceQrRows(space).length > 0
                              ? ` · ${subspaceQrRows(space).length} ${t("subspace QR codes")}`
                              : ""}
                          </small>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>

              <h6 className="mb-2">{t("Layout")}</h6>
              <Row className="g-2">
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">{t("Paper size")}</Label>
                    <Input
                      type="select"
                      value={options.paperSize}
                      onChange={(e) =>
                        updateOption("paperSize", e.target.value)
                      }
                      bsSize="sm"
                    >
                      {Object.keys(PAPER_SIZES).map((key) => (
                        <option key={key} value={key}>
                          {PAPER_SIZES[key].label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">{t("Orientation")}</Label>
                    <Input
                      type="select"
                      value={options.orientation}
                      onChange={(e) =>
                        updateOption("orientation", e.target.value)
                      }
                      bsSize="sm"
                    >
                      <option value="portrait">{t("Portrait")}</option>
                      <option value="landscape">{t("Landscape")}</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">{t("Cell width")} (mm)</Label>
                    <Input
                      type="number"
                      min={5}
                      max={200}
                      step={0.5}
                      value={options.cellWidth}
                      onChange={(e) =>
                        updateOption(
                          "cellWidth",
                          parseFloat(e.target.value) || 5,
                        )
                      }
                      bsSize="sm"
                    />
                    <small className="text-muted">
                      {t("Outer size of each cell (line to line)")}
                    </small>
                  </FormGroup>
                </Col>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">
                      {t("Cell height")} (mm)
                    </Label>
                    <Input
                      type="number"
                      min={5}
                      max={300}
                      step={0.5}
                      value={options.cellHeight}
                      onChange={(e) =>
                        updateOption(
                          "cellHeight",
                          parseFloat(e.target.value) || 5,
                        )
                      }
                      bsSize="sm"
                    />
                    <small className="text-muted">
                      {t("Outer size of each cell (line to line)")}
                    </small>
                  </FormGroup>
                </Col>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">
                      {t("Page margin")} (mm)
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      max={40}
                      step={0.5}
                      value={options.pageMargin}
                      onChange={(e) =>
                        updateOption(
                          "pageMargin",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      bsSize="sm"
                    />
                  </FormGroup>
                </Col>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">
                      {t("Inner padding")} (mm)
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      max={20}
                      step={0.5}
                      value={options.cellPadding}
                      onChange={(e) =>
                        updateOption(
                          "cellPadding",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      bsSize="sm"
                    />
                    <small className="text-muted">
                      {t("Inside the cell — does not add to outer size")}
                    </small>
                  </FormGroup>
                </Col>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">{t("QR size")} (mm)</Label>
                    <Input
                      type="number"
                      min={5}
                      max={80}
                      step={1}
                      disabled={layout.qrAuto}
                      value={layout.qrAuto ? "" : (options.qrSize ?? "")}
                      placeholder={
                        layout.qrAuto
                          ? `${layout.qrSize.toFixed(1)} (auto)`
                          : undefined
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "") {
                          updateOption("qrSize", null);
                          return;
                        }
                        updateOption("qrSize", parseFloat(v) || 5);
                      }}
                      bsSize="sm"
                    />
                    <div className="form-check mt-1 mb-0">
                      <Input
                        className="form-check-input"
                        type="checkbox"
                        id="pdfQrAuto"
                        checked={layout.qrAuto}
                        onChange={(e) =>
                          updateOption(
                            "qrSize",
                            e.target.checked ? null : layout.maxQrSize,
                          )
                        }
                      />
                      <Label
                        className="form-check-label small"
                        htmlFor="pdfQrAuto"
                      >
                        {t("Fit QR to cell")}
                      </Label>
                    </div>
                    {!layout.qrAuto && layout.qrSize < options.qrSize && (
                      <small className="text-muted">
                        {t("Fits at")} {layout.qrSize.toFixed(1)} mm
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col xs="6">
                  <FormGroup className="mb-2">
                    <Label className="small mb-1">{t("Text size")} (pt)</Label>
                    <Input
                      type="number"
                      min={4}
                      max={24}
                      step={0.5}
                      value={options.fontSize}
                      onChange={(e) =>
                        updateOption(
                          "fontSize",
                          parseFloat(e.target.value) || 4,
                        )
                      }
                      bsSize="sm"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <div className="form-check mb-2">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id="pdfIncludeLogo"
                  checked={options.includeLogo}
                  onChange={(e) =>
                    updateOption("includeLogo", e.target.checked)
                  }
                />
                <Label className="form-check-label" htmlFor="pdfIncludeLogo">
                  {t("Include site logo in QR")}
                </Label>
              </div>
              <div className="form-check mb-2">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id="pdfShowBorders"
                  checked={options.showBorders}
                  onChange={(e) =>
                    updateOption("showBorders", e.target.checked)
                  }
                />
                <Label className="form-check-label" htmlFor="pdfShowBorders">
                  {t("Show cutting guides")}
                </Label>
              </div>
              {layout.cellClamped && (
                <small className="text-warning d-block mb-2">
                  {t("Cell size was reduced to fit the page")}:{" "}
                  {layout.cellWidth.toFixed(1)}×{layout.cellHeight.toFixed(1)}{" "}
                  mm
                </small>
              )}
              <small className="text-muted d-block">
                {t(
                  "Each cell outer size is your width x height (line to line)",
                )}
                . {t("Print at 100% / Actual size")}.
              </small>
            </Col>

            <Col lg="7">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">{t("Preview")}</h6>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    size="sm"
                    color="light"
                    disabled={previewPage <= 0}
                    onClick={() => setPreviewPage((p) => Math.max(0, p - 1))}
                  >
                    <i className="ri-arrow-left-s-line" />
                  </Button>
                  <small className="text-muted">
                    {t("Page")} {previewPage + 1} / {pageCount}
                  </small>
                  <Button
                    size="sm"
                    color="light"
                    disabled={previewPage >= pageCount - 1}
                    onClick={() =>
                      setPreviewPage((p) => Math.min(pageCount - 1, p + 1))
                    }
                  >
                    <i className="ri-arrow-right-s-line" />
                  </Button>
                </div>
              </div>

              <div
                className="bg-light border rounded p-2 d-flex justify-content-center"
                style={{ minHeight: 360 }}
              >
                <div
                  style={{
                    width: "100%",
                    maxWidth: options.orientation === "landscape" ? 560 : 360,
                    aspectRatio: previewAspect,
                    background: "#fff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                    position: "relative",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  {/* Cutting guides — same pitch math as the PDF */}
                  {options.showBorders && (
                    <svg
                      width="100%"
                      height="100%"
                      viewBox={`0 0 ${layout.pageWidth} ${layout.pageHeight}`}
                      style={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                      }}
                      aria-hidden="true"
                    >
                      {Array.from({ length: layout.columns + 1 }).map(
                        (_, c) => {
                          const x = layout.pageMargin + c * layout.cellWidth;
                          return (
                            <line
                              key={`v-${c}`}
                              x1={x}
                              y1={layout.pageMargin}
                              x2={x}
                              y2={layout.pageMargin + layout.gridHeight}
                              stroke="#000"
                              strokeWidth={0.15}
                            />
                          );
                        },
                      )}
                      {Array.from({ length: layout.rows + 1 }).map((_, r) => {
                        const y = layout.pageMargin + r * layout.cellHeight;
                        return (
                          <line
                            key={`h-${r}`}
                            x1={layout.pageMargin}
                            y1={y}
                            x2={layout.pageMargin + layout.gridWidth}
                            y2={y}
                            stroke="#000"
                            strokeWidth={0.15}
                          />
                        );
                      })}
                      <text
                        x={layout.pageMargin + layout.cellWidth / 2}
                        y={Math.max(3, layout.pageMargin - 1.2)}
                        textAnchor="middle"
                        fontSize="3"
                        fill="#888"
                      >
                        {layout.cellWidth} mm
                      </text>
                      <text
                        x={Math.max(2.5, layout.pageMargin - 1.2)}
                        y={layout.pageMargin + layout.cellHeight / 2}
                        textAnchor="middle"
                        fontSize="3"
                        fill="#888"
                        transform={`rotate(-90 ${Math.max(2.5, layout.pageMargin - 1.2)} ${layout.pageMargin + layout.cellHeight / 2})`}
                      >
                        {layout.cellHeight} mm
                      </text>
                    </svg>
                  )}

                  <div
                    style={{
                      position: "absolute",
                      left: `${(layout.pageMargin / layout.pageWidth) * 100}%`,
                      top: `${(layout.pageMargin / layout.pageHeight) * 100}%`,
                      display: "grid",
                      gridTemplateColumns: `repeat(${layout.columns}, ${layout.cellWidth}fr)`,
                      gridTemplateRows: `repeat(${layout.rows}, ${layout.cellHeight}fr)`,
                      width: `${(layout.gridWidth / layout.pageWidth) * 100}%`,
                      height: `${(layout.gridHeight / layout.pageHeight) * 100}%`,
                    }}
                  >
                    {Array.from({ length: layout.perPage }).map((_, i) => {
                      const item = previewItems[i];
                      return (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: `${Math.max(1, layout.cellPadding)}px`,
                            overflow: "hidden",
                            minHeight: 0,
                            boxSizing: "border-box",
                          }}
                        >
                          {item ? (
                            <>
                              <div
                                style={{
                                  flex: "0 1 auto",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minHeight: 0,
                                  width: "100%",
                                }}
                              >
                                <QRCode
                                  value={`${QR_BASE_URL}/menu/${item.qrKey}`}
                                  size={previewQrPx}
                                  eyeRadius={4}
                                  ecLevel="H"
                                  bgColor="#fff"
                                  logoPaddingStyle="circle"
                                  logoImage={
                                    options.includeLogo
                                      ? item.site_image || undefined
                                      : undefined
                                  }
                                  enableCORS={true}
                                  crossOrigin="anonymous"
                                  quietZone={2}
                                />
                              </div>
                              <div
                                style={{
                                  fontSize: previewFontPx,
                                  fontWeight: 700,
                                  textAlign: "center",
                                  lineHeight: 1.1,
                                  marginTop: 2,
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={item.name}
                              >
                                {item.name}
                              </div>
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <small className="text-muted d-block mt-2">
                {t("Outer cell")}{" "}
                <strong>
                  {Number(layout.cellWidth).toFixed(1)}×
                  {Number(layout.cellHeight).toFixed(1)} mm
                </strong>{" "}
                · {t("QR")} {layout.qrSize.toFixed(1)} mm · {layout.columns}×
                {layout.rows} ({layout.perPage} {t("per page")}) ·{" "}
                {selectedSpaces.length} {t("spaces")} · {pdfItems.length}{" "}
                {t("QR codes")} · {pageCount} {t("page(s)")}
              </small>
            </Col>
          </Row>
        )}

        {/* High-res QRs only while exporting — avoids freezing the selector */}
        {renderForExport &&
          pdfItems.map((item) => (
            <div
              key={`pdf-qr-${item.id}-${options.includeLogo ? "logo" : "plain"}`}
              ref={(el) => {
                qrRefs.current[item.id] = el;
              }}
              style={{
                position: "fixed",
                left: -9999,
                top: 0,
                pointerEvents: "none",
                opacity: 0,
              }}
              aria-hidden="true"
            >
              <QRCode
                value={`${QR_BASE_URL}/menu/${item.qrKey}`}
                size={512}
                eyeRadius={10}
                ecLevel="H"
                bgColor="#fff"
                quietZone={0}
                logoPaddingStyle="circle"
                logoImage={
                  options.includeLogo
                    ? item.site_image || undefined
                    : undefined
                }
                enableCORS={true}
                crossOrigin="anonymous"
              />
            </div>
          ))}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isGenerating}>
          {t("Cancel")}
        </Button>
        <Button
          className="text-white"
          style={{ backgroundColor: "#c59253", borderColor: "#c59253" }}
          onClick={handleGenerate}
          disabled={
            isLoadingSpaces || pdfItems.length === 0 || isGenerating
          }
        >
          {isGenerating ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t("Generating...")}
            </>
          ) : (
            <>
              <i className="ri-file-pdf-line me-1" />
              {t("Download PDF")} ({pdfItems.length})
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExportQRPdfModal;
